import {
  createFileRoute,
  useNavigate,
  useRouter,
} from '@tanstack/react-router';
import { SEO } from 'components/Seo/Seo';
import { useAppDispatch } from 'hooks/useAppDispatch';
import useAppLanguage from 'hooks/useAppLanguage';
import { useOtpEnabled } from 'hooks/useOtpStatus';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { initiateSmsOtp } from 'store/otp/otp-actions';
import { RootState } from 'store/reducer';
import trimZero from 'utils/mobile-number';
import EnterDetailsView from './components/EnterDetailsView';
import EnterOtpView from './components/EnterOtpView';
import messages from './messages';

export const Route = createFileRoute('/_authenticated/settings/otp/enable')({
  component: EnableOtp,
});

function EnableOtp() {
  const navigate = useNavigate();
  const router = useRouter();
  const dispatch = useAppDispatch();
  const language = useAppLanguage();
  const otpEnabled = useOtpEnabled();

  const [stepNumber, setStepNumber] = useState<number>(1);
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [countryCode, setCountryCode] = useState<string>('');
  const [dialCode, setDialCode] = useState<string>('');
  const [requestError, setRequestError] = useState<string>('');

  const { loading, initiateSmsOtpError } = useSelector(
    (rootState: RootState) => rootState.otp,
  );

  const seoMessages = messages[language].seo;
  const viewMessage = messages[language];
  const { enableOTPMessages } = messages[language];

  useEffect(() => {
    if (otpEnabled) navigate({ to: '/settings' });
  }, [history, otpEnabled]);

  const onContinue = () => {
    setStepNumber((oldState: number) => oldState + 1);
  };

  const handlePageBack = () => {
    if (stepNumber > 1) {
      setStepNumber((prev: number) => prev - 1);
    } else router.history.back();
  };

  const onChangePhoneNumber = (number: string) => {
    if (typeof number !== 'undefined') {
      setPhoneNumber(trimZero(number));
    }
  };

  const onChangeCountryCode = (code: string) => {
    setCountryCode(code);
  };

  const onSubmitDetails = async () => {
    dispatch(initiateSmsOtp(countryCode, phoneNumber));
    if (!initiateSmsOtpError && !loading) {
      onContinue();
    } else {
      setRequestError('Request was unsuccessful. Please try again');
    }
  };

  const renderActiveStep = () => {
    switch (stepNumber) {
      case 1:
      default:
        return (
          <EnterDetailsView
            title={viewMessage.enterDetails.title}
            subtitle={viewMessage.enterDetails.subtitle}
            buttonText={viewMessage.enterDetails.next}
            onSubmitDetails={onSubmitDetails}
            onChangePhoneNumber={onChangePhoneNumber}
            onChangeCountryCode={onChangeCountryCode}
            phoneNumber={phoneNumber}
            country={countryCode}
            countryDialCode={dialCode}
            onSetCountryDialCode={setDialCode}
            requestError={requestError}
            addMarginTop
          />
        );
      case 2:
        return (
          <EnterOtpView
            sendOtp={() => dispatch(initiateSmsOtp(countryCode, phoneNumber))}
            onContinue={onContinue}
            messages={viewMessage}
            phoneNumber={`+${dialCode}${phoneNumber}`}
            isLoading={loading}
          />
        );
      case 3:
        navigate({ to: '/settings' });
    }
  };

  return (
    <>
      <div className="static lg:relative w-full">
        <SEO
          title={seoMessages.title}
          description={seoMessages.description}
          keywords={seoMessages.keywords}
          page="settings/otp"
        />
        {stepNumber < 1 ? null : (
          <button
            type="button"
            onClick={() => handlePageBack()}
            className="absolute lg:fixed mt-2 ml-6 text-2xl lg:mt-10 lg:ml-6 lg:text-base left-0 top-0 lg:top-auto lg:left-auto z-40 text-header-text focus:outline-none font-semibold">
            <span className="block lg:hidden">←</span>
            <span className="hidden lg:block">{enableOTPMessages.back}</span>
          </button>
        )}
        <div className="px-6 py-4 flex flex-col justify-center lg:items-center">
          {renderActiveStep()}
        </div>
      </div>
    </>
  );
}

export default EnableOtp;
